/**
 * Shared orchestration for the hide-on-mark recap rows (#967 review) — the
 * Home recap section and the Events-tab recap strip, which differ ONLY in
 * their scope literals. One hook per level:
 *
 * `useRecapRow(events, { markScope })` — the ROW half:
 *   - `openEventId` state (at most one roster drawer — it is modal). The open
 *     entry is EXEMPT from the hidden filter (row-level `deferWhile`), so a
 *     mark landing mid-flow can never unmount the open drawer; on close the
 *     filter re-runs and slot + card collapse together.
 *   - the `visible` pre-filter, so a hidden entry contributes no SwipeRow
 *     slot and no impression (a null-rendering child would still count as a
 *     row item). Two hidden sources, both pure/passive:
 *       1. persisted mark-hide records on `markScope`, keyed by the current
 *          viewer (no user → nothing hidden); subscribed to landed marks, so
 *          a mark re-runs the filter;
 *       2. the derived event-level truth: ≥1 viewer mark in the connections
 *          cache (ONE passive read — `enabled: false`, never a fetch), so a
 *          mark made on ANY surface or device hides here too.
 *   - NO entries while an authenticated viewer is still resolving (auth UI is
 *     up, `/users/me` not yet): the user-keyed read above is blind without
 *     a user id, so a card the viewer already marked would mount, count an
 *     impression, then vanish. A guest (no auth UI) is a real, final "no
 *     user" — the strip shows them ended events and nothing is keyed.
 *   - ONE feed-counts batch across every visible entry (the T-8 contract:
 *     one call per surface, never per card).
 *
 * `useRecapCard(event, { markScope, surface, rosterOpen })` — the CARD half:
 *   `useRecapMarkHidden` (writer + canonical per-card gate, `deferWhile` =
 *   the open drawer) plus the once-per-mount `live_surface_displayed`
 *   impression that a hidden card never fires (`useLiveImpression`). The
 *   caller renders null when `hidden` — belt to the row's pre-filter; the
 *   two read the same sources through the same chokepoints, so they cannot
 *   disagree for more than a render.
 *
 * Scopes stay the caller's: the persisted records must not cross surfaces
 * (marking on Home must not blank the strip's record — the DERIVED source is
 * what crosses, deliberately, as event-level truth).
 */
import { useMemo, useState, useSyncExternalStore } from 'react';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { useEventFeedCounts } from '@hooks/useEventFeedCounts';
import { useLiveImpression } from '@hooks/useLiveImpression';
import {
  readRecapMarkHidden,
  readRecapMarksVersion,
  selectViewerHasEventMark,
  subscribeRecapMarks,
  useRecapMarkHidden,
  type RecapMarkScope,
} from '@hooks/useRecapMarkHidden';
import { useWeMetConnectionsList } from '@hooks/useWeMetConnectionsV2';
import type { LiveSurface } from '@lib/analytics-events';
import type { EventRead } from '@models/api';
import { useAuth } from '@providers';

/** Stable empty row for the unresolved-viewer window (never mutated). */
const NO_ENTRIES: EventRead[] = [];

export interface UseRecapRowOptions {
  /** The surface's own persisted mark-hide scope. */
  markScope: RecapMarkScope;
}

export interface UseRecapRowResult {
  /** Events that should occupy row slots — hidden filtered, the open-drawer entry exempt. */
  visible: EventRead[];
  /** Which entry's roster drawer is open (at most one — the drawer is modal). */
  openEventId: string | null;
  setOpenEventId: (eventId: string | null) => void;
  /** Per-id feed count from the row's ONE batch (T-8; `undefined` = nothing to show). */
  getFeedCount: (eventId: string) => number | undefined;
  /** Per-id skeleton window of that batch (first fetch, batched ids only). */
  isCountPending: (eventId: string) => boolean;
}

export function useRecapRow(
  events: readonly EventRead[],
  { markScope }: UseRecapRowOptions,
): UseRecapRowResult {
  const { user } = useCurrentUser();
  const userId = user?.id;
  // Lagged flag (not `isAuthenticated`, which already needs the user): true
  // while auth settles for a returning viewer, so it marks the unresolved window.
  const { showAuthenticatedUI } = useAuth();
  const viewerPending = showAuthenticatedUI && !userId;
  const [openEventId, setOpenEventId] = useState<string | null>(null);

  // Passive cache read (`enabled: false` — the useRecapMarkHidden idiom): the
  // row never fires the auth-only connections request; it reacts to whatever
  // the drawers / connections tab already loaded.
  const { data: connections } = useWeMetConnectionsList({ enabled: false });

  // A landed mark has no other re-render path once the drawer is closed. The
  // snapshot is a version counter; it is a memo dep so the compiler can't reuse
  // a filter result from before the mark.
  const marksVersion = useSyncExternalStore(subscribeRecapMarks, readRecapMarksVersion);

  const visible = useMemo(
    () =>
      viewerPending
        ? NO_ENTRIES
        : events.filter(
            event =>
              event.id === openEventId ||
              (!readRecapMarkHidden(userId, event.id, markScope) &&
                !selectViewerHasEventMark(connections, event.id)),
          ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- marksVersion invalidates the impure persisted read
    [viewerPending, events, openEventId, userId, markScope, connections, marksVersion],
  );

  // ONE feed-counts batch for the row (T-8): every visible card's id at once
  // — never a fetch per card. Recap rows are ended events, so every id counts.
  const { getFeedCount, isCountPending } = useEventFeedCounts(visible.map(event => event.id));

  return { visible, openEventId, setOpenEventId, getFeedCount, isCountPending };
}

export interface UseRecapCardOptions {
  /** The surface's persisted mark-hide scope — pass the row's `markScope`. */
  markScope: RecapMarkScope;
  /** Analytics surface for the impression (spec §9). */
  surface: LiveSurface;
  /** The card's roster-drawer visibility — the row owns it (row-level deferWhile). */
  rosterOpen: boolean;
}

export function useRecapCard(
  event: EventRead,
  { markScope, surface, rosterOpen }: UseRecapCardOptions,
): { hidden: boolean; onMarked: () => void } {
  // Canonical hide-on-mark wiring (§2/§3): opening never hides; a landed mark
  // (drawer onMarked) hides after the drawer closes (deferWhile); an
  // already-marked event derives hidden passively inside the hook too.
  const { hidden, onMarked } = useRecapMarkHidden(event.id, markScope, {
    deferWhile: rosterOpen,
  });

  // Impression, once per mount per event (spec §9): re-renders are guard-set
  // no-ops and a hidden card never counts. Phase is a CONSTANT on these two
  // surfaces — both rows are sourced from ended-event feeds inside the 48h
  // recap window — but it is stamped all the same so every surface the shared
  // hook serves carries the dimension (see useLiveImpression's header).
  useLiveImpression(surface, event, { phase: 'ended-recap', skipWhen: hidden });

  return { hidden, onMarked };
}
